/**
 * Tracker Data Hooks
 * React Query hooks for fetching and caching tracker data (MTH-052)
 * Updated for project-centric architecture
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  projectBoardsApi,
  projectIssuesApi,
  initiativesApi,
  statisticsApi,
  type Issue,
} from '../api/work';

/**
 * Fetch boards for a specific project with caching
 */
export function useBoards(projectId: string | undefined) {
  return useQuery({
    queryKey: ['boards', projectId],
    queryFn: () => projectBoardsApi.list(projectId!),
    enabled: !!projectId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch a specific board with caching
 */
export function useBoard(projectId: string | undefined, boardId: string) {
  return useQuery({
    queryKey: ['board', projectId, boardId],
    queryFn: () => projectBoardsApi.get(projectId!, boardId),
    enabled: !!(projectId && boardId),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch issues for a specific board with caching
 * This is the main data-heavy query
 */
export function useIssues(projectId: string | undefined, boardId: string) {
  return useQuery({
    queryKey: ['issues', projectId, boardId],
    queryFn: () => projectIssuesApi.list(projectId!, boardId),
    enabled: !!(projectId && boardId),
    staleTime: 30000, // 30 seconds - issues update frequently
  });
}

/**
 * Fetch initiatives for a project with caching
 */
export function useInitiatives(projectId: string | undefined) {
  return useQuery({
    queryKey: ['initiatives', projectId],
    queryFn: () => initiativesApi.list(projectId!),
    enabled: !!projectId,
    staleTime: 30000,
  });
}

/**
 * Fetch board statistics with caching
 */
export function useBoardStats(projectId: string | undefined, boardId: string) {
  return useQuery({
    queryKey: ['board-stats', projectId, boardId],
    queryFn: () => statisticsApi.getBoardStats(projectId!, boardId),
    enabled: !!(projectId && boardId),
    staleTime: 30000,
  });
}

/**
 * Mutation hook for creating issues
 */
export function useCreateIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      boardId,
      data,
    }: {
      projectId: string;
      boardId: string;
      data: Parameters<typeof projectIssuesApi.create>[2];
    }) => projectIssuesApi.create(projectId, boardId, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch issues
      queryClient.invalidateQueries({
        queryKey: ['issues', variables.projectId, variables.boardId],
      });
      queryClient.invalidateQueries({
        queryKey: ['board', variables.projectId, variables.boardId],
      });
    },
  });
}

/**
 * Mutation hook for moving issues
 */
export function useMoveIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      boardId,
      issueId,
      status,
      index,
    }: {
      projectId: string;
      boardId: string;
      issueId: string;
      status: string;
      index?: number;
    }) => projectIssuesApi.move(projectId, boardId, issueId, status, index),
    onSuccess: (_, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ['issues', variables.projectId, variables.boardId],
      });
      queryClient.invalidateQueries({
        queryKey: ['board', variables.projectId, variables.boardId],
      });
    },
  });
}

/**
 * Mutation hook for updating issues
 */
export function useUpdateIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      boardId,
      issueId,
      data,
    }: {
      projectId: string;
      boardId: string;
      issueId: string;
      data: Partial<Issue>;
    }) => projectIssuesApi.update(projectId, boardId, issueId, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ['issues', variables.projectId, variables.boardId],
      });
    },
  });
}

/**
 * Mutation hook for creating boards
 */
export function useCreateBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: Parameters<typeof projectBoardsApi.create>[1];
    }) => projectBoardsApi.create(projectId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['boards', variables.projectId] });
    },
  });
}

/**
 * @deprecated Worktrees are deprecated. Use useBoards with projectId instead.
 */
export function useWorktrees() {
  return useQuery({
    queryKey: ['worktrees'],
    queryFn: () => Promise.resolve([]),
    staleTime: Infinity,
  });
}
